<template>
  <div class="kanji-study">
    <div v-if="loading" class="loading">Loading...</div>
    
    <!-- Study Session -->
    <div v-else-if="studyMode && currentIndex < studyItems.length" class="study-session">
      <!-- Progress header -->
      <div class="progress-header">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <div class="stats-row">
          <div class="stat">{{ currentIndex + 1 }} / {{ studyItems.length }}</div>
          <div class="stat">Streak: <span class="streak">{{ streak }}</span></div>
        </div>
      </div>

      <!-- Kanji display -->
      <transition name="flip" mode="out-in">
        <div :key="currentIndex" class="kanji-card">
          <div class="kanji-character">{{ currentKanji.character }}</div>
          <div v-if="currentKanji.status === 'review'" class="badge review">Review</div>
          <div v-else class="badge new">New</div>
        </div>
      </transition>

      <!-- Question -->
      <div class="question">What does this kanji mean?</div>

      <!-- Options -->
      <div class="options-grid">
        <button
          v-for="(option, idx) in options"
          :key="idx"
          class="option-btn"
          :class="{
            'selected': selectedOption === idx,
            'correct': showingAnswer && option.correct,
            'incorrect': showingAnswer && selectedOption === idx && !option.correct
          }"
          :disabled="showingAnswer"
          @click="selectOption(idx)"
        >
          {{ option.text }}
        </button>
      </div>

      <!-- Additional Info (shown after answer) -->
      <transition name="fade">
        <div v-if="showingAnswer && currentKanji" class="kanji-info">
          <div class="info-section">
            <h4>Readings</h4>
            <div v-if="currentKanji.onyomi && currentKanji.onyomi.length">
              <strong>On'yomi:</strong> {{ currentKanji.onyomi.join(', ') }}
            </div>
            <div v-if="currentKanji.kunyomi && currentKanji.kunyomi.length">
              <strong>Kun'yomi:</strong> {{ currentKanji.kunyomi.join(', ') }}
            </div>
          </div>
          <div class="info-section">
            <div><strong>Strokes:</strong> {{ currentKanji.strokes }}</div>
            <div v-if="currentKanji.grade"><strong>Grade:</strong> {{ currentKanji.grade }}</div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Session Complete -->
    <div v-else-if="studyMode && currentIndex >= studyItems.length" class="session-complete">
      <h2>Session Complete! 🎉</h2>
      <div class="final-stats">
        <div class="stat-box">
          <div class="stat-value correct">{{ sessionCorrect }}</div>
          <div class="stat-label">Correct</div>
        </div>
        <div class="stat-box">
          <div class="stat-value incorrect">{{ sessionIncorrect }}</div>
          <div class="stat-label">Incorrect</div>
        </div>
        <div class="stat-box">
          <div class="stat-value">{{ sessionAccuracy }}%</div>
          <div class="stat-label">Accuracy</div>
        </div>
      </div>
      <div class="rank">{{ getRank() }}</div>
      <button @click="loadStudySet" class="action-btn">Next Session</button>
      <button @click="studyMode = false" class="action-btn secondary">Dashboard</button>
    </div>

    <!-- Dashboard -->
    <div v-else class="dashboard">
      <h1>Kanji Study</h1>
      
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-number">{{ reviewCount }}</div>
          <div class="stat-label">Reviews Due</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ totalStudied }}</div>
          <div class="stat-label">Total Studied</div>
        </div>
      </div>

      <!-- SRS Distribution -->
      <div v-if="srsDistribution" class="srs-distribution">
        <h3>SRS Distribution</h3>
        <div class="distribution-grid">
          <div class="dist-item apprentice">
            <div class="dist-count">{{ srsDistribution.apprentice || 0 }}</div>
            <div class="dist-label">Apprentice</div>
          </div>
          <div class="dist-item guru">
            <div class="dist-count">{{ srsDistribution.guru || 0 }}</div>
            <div class="dist-label">Guru</div>
          </div>
          <div class="dist-item master">
            <div class="dist-count">{{ srsDistribution.master || 0 }}</div>
            <div class="dist-label">Master</div>
          </div>
          <div class="dist-item enlightened">
            <div class="dist-count">{{ srsDistribution.enlightened || 0 }}</div>
            <div class="dist-label">Enlightened</div>
          </div>
          <div class="dist-item burned">
            <div class="dist-count">{{ srsDistribution.burned || 0 }}</div>
            <div class="dist-label">Burned</div>
          </div>
        </div>
      </div>

      <button @click="startStudy" class="start-btn" :disabled="!canStudy">
        {{ canStudy ? 'Start Study Session' : 'No items to study' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import confetti from 'canvas-confetti';

const loading = ref(true);
const studyMode = ref(false);
const studyItems = ref([]);
const currentIndex = ref(0);
const options = ref([]);
const selectedOption = ref(null);
const showingAnswer = ref(false);
const sessionCorrect = ref(0);
const sessionIncorrect = ref(0);
const streak = ref(0);
const srsDistribution = ref(null);

const token = localStorage.getItem('token');

const currentKanji = computed(() => studyItems.value[currentIndex.value] || {});
const progressPercent = computed(() => (currentIndex.value / studyItems.value.length) * 100);
const reviewCount = computed(() => studyItems.value.filter(i => i.status === 'review').length);
const totalStudied = computed(() => studyItems.value.length);
const canStudy = computed(() => studyItems.value.length > 0);
const sessionTotal = computed(() => sessionCorrect.value + sessionIncorrect.value);
const sessionAccuracy = computed(() => 
  sessionTotal.value > 0 ? Math.round((sessionCorrect.value / sessionTotal.value) * 100) : 0
);

onMounted(async () => {
  await loadStats();
  await loadStudySet();
  loading.value = false;
});

async function loadStats() {
  if (!token) return;
  try {
    const res = await axios.get('/api/progress/stats', {
      headers: { 'x-auth-token': token }
    });
    srsDistribution.value = res.data.data.srsDistribution || {};
  } catch (err) {
    console.error('Failed to load stats:', err);
  }
}

async function loadStudySet() {
  if (!token) return;
  try {
    const res = await axios.get('/api/kanji/study', {
      headers: { 'x-auth-token': token }
    });
    studyItems.value = res.data.data || [];
    currentIndex.value = 0;
    sessionCorrect.value = 0;
    sessionIncorrect.value = 0;
    streak.value = 0;
  } catch (err) {
    console.error('Failed to load study set:', err);
  }
}

function startStudy() {
  if (!canStudy.value) return;
  studyMode.value = true;
  prepareQuestion();
}

function prepareQuestion() {
  if (!currentKanji.value || !currentKanji.value.meanings) return;
  
  selectedOption.value = null;
  showingAnswer.value = false;
  
  const correctMeaning = currentKanji.value.meanings[0];
  const distractors = getDistractors(correctMeaning);
  
  const allOptions = [
    { text: correctMeaning, correct: true },
    ...distractors.map(d => ({ text: d, correct: false }))
  ];
  
  // Shuffle options
  options.value = allOptions.sort(() => Math.random() - 0.5);
}

function getDistractors(correctMeaning) {
  const pool = studyItems.value
    .filter(k => k.meanings && k.meanings[0] !== correctMeaning)
    .map(k => k.meanings[0]);
  
  const distractors = [];
  while (distractors.length < 3 && pool.length > 0) {
    const idx = Math.floor(Math.random() * pool.length);
    distractors.push(pool.splice(idx, 1)[0]);
  }
  
  // Fill with generic meanings if needed
  const generic = ['Sun', 'Moon', 'Tree', 'Water', 'Fire', 'Mountain', 'River', 'Person', 'Book', 'School'];
  while (distractors.length < 3) {
    const g = generic[Math.floor(Math.random() * generic.length)];
    if (!distractors.includes(g) && g !== correctMeaning) {
      distractors.push(g);
    }
  }
  
  return distractors.slice(0, 3);
}

async function selectOption(idx) {
  if (showingAnswer.value) return;
  
  selectedOption.value = idx;
  showingAnswer.value = true;
  
  const isCorrect = options.value[idx].correct;
  
  if (isCorrect) {
    sessionCorrect.value++;
    streak.value++;
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  } else {
    sessionIncorrect.value++;
    streak.value = 0;
  }
  
  // Update progress on server
  try {
    await axios.post('/api/kanji/review',
      {
        kanjiId: currentKanji.value._id,
        correct: isCorrect
      },
      { headers: { 'x-auth-token': token } }
    );
  } catch (err) {
    console.error('Failed to update progress:', err);
  }
  
  // Move to next after delay
  setTimeout(() => {
    currentIndex.value++;
    if (currentIndex.value < studyItems.value.length) {
      prepareQuestion();
    }
  }, 2500);
}

function getRank() {
  const pct = sessionAccuracy.value;
  if (pct === 100) return '🏆 Perfect! Kanji Master!';
  if (pct >= 80) return '⭐ Excellent Work!';
  if (pct >= 60) return '👍 Good Job!';
  return '📚 Keep Practicing!';
}
</script>

<style scoped>
.kanji-study {
  max-width: 700px;
  margin: 2rem auto;
  padding: 1rem;
}

.loading {
  text-align: center;
  font-size: 1.5rem;
  padding: 3rem;
}

/* Study Session */
.study-session {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
}

.progress-header {
  margin-bottom: 2rem;
}

.progress-bar {
  height: 10px;
  background: #e0e0e0;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4f8cff, #3a75e6);
  transition: width 0.5s ease;
}

.stats-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.95rem;
  color: #666;
}

.streak {
  font-weight: bold;
  color: #f59e0b;
}

.kanji-card {
  text-align: center;
  padding: 3rem 2rem;
  position: relative;
}

.kanji-character {
  font-size: 10rem;
  font-weight: bold;
  color: #2c3e50;
  text-shadow: 3px 3px 0px #eee;
}

.badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.badge.review {
  background: #fff3cd;
  color: #856404;
}

.badge.new {
  background: #d1ecf1;
  color: #0c5460;
}

.question {
  text-align: center;
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 1.5rem 0;
}

.options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin: 2rem 0;
}

.option-btn {
  padding: 1.5rem;
  font-size: 1.1rem;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

.option-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: #4f8cff;
  box-shadow: 0 4px 8px rgba(79, 140, 255, 0.2);
}

.option-btn.selected {
  border-color: #4f8cff;
}

.option-btn.correct {
  background: #d4edda;
  border-color: #28a745;
  color: #155724;
}

.option-btn.incorrect {
  background: #f8d7da;
  border-color: #dc3545;
  color: #721c24;
}

.option-btn:disabled {
  cursor: not-allowed;
}

.kanji-info {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
}

.info-section {
  margin: 1rem 0;
}

.info-section h4 {
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

/* Session Complete */
.session-complete {
  text-align: center;
  padding: 3rem 2rem;
}

.session-complete h2 {
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 2rem;
}

.final-stats {
  display: flex;
  justify-content: space-around;
  margin: 2rem 0;
}

.stat-box {
  text-align: center;
}

.stat-value {
  font-size: 3rem;
  font-weight: bold;
  color: #2c3e50;
}

.stat-value.correct {
  color: #28a745;
}

.stat-value.incorrect {
  color: #dc3545;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.5rem;
}

.rank {
  font-size: 1.5rem;
  font-weight: 600;
  color: #4f8cff;
  margin: 1.5rem 0;
}

.action-btn {
  margin: 0.5rem;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  background: #4f8cff;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: #3a75e6;
  transform: translateY(-1px);
}

.action-btn.secondary {
  background: #6c757d;
}

.action-btn.secondary:hover {
  background: #5a6268;
}

/* Dashboard */
.dashboard {
  text-align: center;
}

.dashboard h1 {
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 2rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin: 2rem 0;
}

.stat-card {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.stat-number {
  font-size: 3rem;
  font-weight: bold;
  color: #4f8cff;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.5rem;
}

.srs-distribution {
  margin: 3rem 0;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.srs-distribution h3 {
  margin-bottom: 1.5rem;
  color: #2c3e50;
}

.distribution-grid {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1rem;
}

.dist-item {
  padding: 1rem;
  border-radius: 8px;
  min-width: 100px;
  text-align: center;
}

.dist-item.apprentice {
  background: #fff3cd;
}

.dist-item.guru {
  background: #d1ecf1;
}

.dist-item.master {
  background: #d4edda;
}

.dist-item.enlightened {
  background: #e2d9f3;
}

.dist-item.burned {
  background: #f8d7da;
}

.dist-count {
  font-size: 2rem;
  font-weight: bold;
}

.dist-label {
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.25rem;
}

.start-btn {
  margin: 2rem 0;
  padding: 1.5rem 3rem;
  font-size: 1.3rem;
  background: linear-gradient(135deg, #4f8cff, #3a75e6);
  color: white;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(79, 140, 255, 0.3);
  transition: all 0.3s;
}

.start-btn:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(79, 140, 255, 0.4);
}

.start-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}

/* Animations */
.flip-enter-active, .flip-leave-active {
  transition: all 0.4s;
}

.flip-enter-from {
  transform: rotateY(90deg);
  opacity: 0;
}

.flip-leave-to {
  transform: rotateY(-90deg);
  opacity: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 600px) {
  .kanji-character {
    font-size: 6rem;
  }
  
  .options-grid {
    grid-template-columns: 1fr;
  }
  
  .final-stats {
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .distribution-grid {
    flex-direction: column;
  }
}
</style>
